// @flow

import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import StyleSheet from 'stilr';
import { desk, findMatch } from 'react-flexr';

import ValidationDialog from './validation-dialog';
import ActivitiesDialog from './dialog-activities';

import ListSubheader from '@mui/material/ListSubheader';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';

import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';

import Chip from 'app/common/chip';
import Button from 'app/common/button';
import ConfirmDialog from 'app/common/ConfirmDialog';

import i18n from 'app/common/i18n';

import { date } from 'app/lib/format';

import TearSheet from './tear-sheet';

import { approve, reject, toggleActivation } from 'app/redux/modules/dwellings';
import { connect } from 'react-redux';
import { Query } from '../common/Query';
import CheckboxDialog from '../common/CheckboxDialog';
import { SelectField } from 'app/common/form';
import { Mutation } from '../common/Mutation';
import { toastr } from 'app/common/toastr';
// import { goal } from '../../lib/tracking/clicky';
import AdminPanel from './AdminPlanel';
import DwellingsTable from 'app/dwellings/table';

const DWELLING = `
  query DWELLING($dwellingId: ID!) {
    dwelling(id: $dwellingId) {
      user {
        name
        firstname
        lastname
        company
        phone
        type
        email
        emailVerified
        phoneVerified
        dwellings{
          id
          location {
            address
            street
            city
            zip
          }
          status
          active
        }
      }
    }
  }
`;

const UPDATE_DWELLING = `
mutation UpdateDwelling($dwellingId: ID! $input: DwellingInput!) {
  updateDwelling(id: $dwellingId, input: $input) {
    id
    listingType
    useDescriptionAI
  }
}
`;

function withCurrency(value) {
  if (typeof value === 'number' || /^[0-9]+$/.test(value)) {
    const number = String(value).split('.');
    return `${number[0]
      .substr(0, 6)
      .replace(/^([0-9]{1,3})([0-9]{3})+$/, (m, p1, p2) => `${p1}.${p2}`)}${
      number.length === 2 ? `,${number[1]}` : ''
    } kr.`;
  }
  return value;
}

function mapTypeDwelling(type) {
  switch (type) {
    case 'ROOM':
      return 'værelse';
    case 'APARTMENT':
      return 'lejlighed';
    case 'HOUSE':
      return 'villa';
    case 'TOWNHOUSE':
      return 'rækkehus';
    case 'FARMHOUSE':
      return 'landejendom';
    default:
      return 'lejebolig';
  }
}

function serializeTerms(value: '_FALSE' | '_TRUE' | '_NULL'): boolean | null {
  return value === '_TRUE' ? true : value === '_FALSE' ? false : null;
}

const styles = StyleSheet.create({
  mediaContainer: {
    position: 'relative',
    width: '100%',

    ':before': {
      content: '""',
      display: 'block',
      paddingTop: `${56.25}%`
    },
    [desk]: {
      ':before': {
        paddingTop: `${56.25 * 0.75}%`
      }
    }
  },
  mediaContent: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    overflow: 'hidden'
  },
  mediaImage: {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  }
});

type Props = {
  handleChangeMerge: mixed,
  dwelling: mixed, // TODO: immutable Map
  user: mixed, // TODO: immutable Map,
  approve: (string, string) => void,
  reject: (string, string) => void,
  hideActions: boolean
};

@connect(() => ({}), { approve, reject, toggleActivation })
@i18n
class DwellingPreview extends React.Component<Props> {
  i18n: (string) => string;
  state = {
    currentView: 0,
    showNormalDescription: false,
    showAIDescription: false,
    useDescriptionAI: null
  };

  componentDidUpdate(prevProps) {
    const prevDwelling = prevProps.dwelling;
    const nextDwelling = this.props.dwelling;

    if (
      nextDwelling &&
      (!prevDwelling ||
        prevDwelling.get('uuid') !== nextDwelling.get('uuid') ||
        prevDwelling.get('useDescriptionAI') !==
          nextDwelling.get('useDescriptionAI'))
    ) {
      this.setState({
        useDescriptionAI: nextDwelling.get('useDescriptionAI') === true
      });
    }
  }

  handleViewChange = (indices) => {
    const idx = Array.isArray(indices) ? indices[0] : indices;
    if (typeof idx === 'number') {
      this.setState({ currentView: idx });
    }
  };

  handleThumbClick = (idx) => {
    this.setState({ currentView: idx });
  };

  toggleActivation = () => {
    const { dwelling } = this.props;
    this.props.toggleActivation(dwelling.get('uuid'), !dwelling.get('active'));
  };

  handleApprove = (
    reason: string,
    mails: { approval: boolean, followUp: boolean }
  ) => {
    return this.props.approve(this.props.dwelling.get('uuid'), {
      reason,
      mails
    });
  };

  handleReject = (reason: string) => {
    return this.props.reject(this.props.dwelling.get('uuid'), { reason });
  };

  handleEdit = () => {
    this.props.navigate(`/dwellings/${this.props.dwelling.get('uuid')}`);
  };

  handleClose = () => {
    this.props.navigate('/dwellings/');
  };

  extendDwelling = () => {
    this.props.onExtend({ dwellingId: this.props.dwelling.get('uuid') });
    toastr.success(this.props.i18n('annoncering forlænget'));
  };

  getValidationBorderColor = (item) => {
    if (item.valid === true) {
      return '#2e7d32';
    }
    if (item.valid === false) {
      return '#c62828';
    }
    return 'transparent';
  };

  getSelectedBorderColor = (isSelected) => {
    return isSelected ? '#000' : 'transparent';
  };

  render() {
    if (!this.props.dwelling) {
      return null;
    }

    const user = this.props.user;
    const isAdmin =
      (this.props.user && this.props.user.get('role') === 'admin') || false;
    const showActions = isAdmin && !this.props.hideActions;

    const isPalm = findMatch('palm') ? true : false;

    const dwelling = this.props.dwelling;

    const dwellingJs = dwelling.toJS();
    const consents = dwellingJs.consents;
    const location = dwellingJs.location;
    const images = dwellingJs.images || [];
    const anonymous = dwellingJs.anonymous || [];
    const economy = dwellingJs.economy;

    const description = dwelling.getIn(['description']);
    const descriptionAI = dwelling.getIn(['descriptionAI']);
    const useDescriptionAI = dwelling.getIn(['useDescriptionAI']) === true;

    const hasNormalDescription = Boolean(description);
    const hasAIDescription = Boolean(descriptionAI);

    // Only admins are allowed to see AI description
    const showAIByDefault = isAdmin && hasAIDescription && useDescriptionAI;
    const showNormalByDefault =
      hasNormalDescription && (!showAIByDefault || !isAdmin);

    const showNormalDescription = isAdmin
      ? this.state.showNormalDescription || showNormalByDefault
      : hasNormalDescription;

    const showAIDescription = isAdmin
      ? this.state.showAIDescription || showAIByDefault
      : false;

    const zipCity = `${dwelling.getIn(['location', 'zip'])} ${dwelling.getIn([
      'location',
      'city'
    ])}`;

    const showAdminPanel =
      this.props.location.pathname === `/dwellings/${dwellingJs.uuid}/preview`;

    const media = images
      .map((image) =>
        image.path
          ? {
              src: `/${image.path}`,
              valid: typeof image.valid === 'boolean' ? image.valid : null,
              reason: image.reason || null,
              kind: 'image'
            }
          : null
      )
      .concat([
        location.geo
          ? {
              src: `https://map.akut-bolig.dk/staticmap?center=${location.geo.coordinates[0]},${location.geo.coordinates[1]}&markers=${location.geo.coordinates[0]},${location.geo.coordinates[1]},lightblue1&zoom=14&size=865x512&maptype=mapnik`,
              valid: true,
              reason: 'Static Map of Location',
              kind: 'map'
            }
          : null
      ])
      .filter(Boolean);

    const remarks = [];
    if (dwelling.getIn(['available'])) {
      remarks.push(
        `Lejemålet kan overtages fra d. ${date.format(
          new Date(dwelling.getIn(['available']))
        )}.`
      );
    }
    if (dwelling.getIn(['timeframe'])) {
      switch (dwelling.getIn(['timeframe', 'type'])) {
        case 'UNLIMITED':
          remarks.push('Lejemålet er uden tidsbegrænsning.');
          break;
        case 'DATE':
          remarks.push(
            `Lejemålet er tidsbegrænset indtil ${date.format(
              new Date(dwelling.getIn(['timeframe', 'date']))
            )}.`
          );
          break;
        case 'RANGE': {
          if (
            !dwelling.getIn(['timeframe', 'min']) &&
            dwelling.getIn(['timeframe', 'max']) < 49
          ) {
            remarks.push(
              `Lejemålet er tidsbegrænset i maksimum ${dwelling.getIn([
                'timeframe',
                'max'
              ])} måneder.`
            );
          } else if (
            dwelling.getIn(['timeframe', 'min']) &&
            dwelling.getIn(['timeframe', 'max']) === 49
          ) {
            remarks.push(
              `Lejemålet er tidsbegrænset til minimum ${dwelling.getIn([
                'timeframe',
                'min'
              ])} måneder.`
            );
          } else if (
            dwelling.getIn(['timeframe', 'min']) &&
            dwelling.getIn(['timeframe', 'max']) < 49
          ) {
            remarks.push(
              `Lejemålet er tidsbegrænset i ${dwelling.getIn([
                'timeframe',
                'min'
              ])} - ${dwelling.getIn(['timeframe', 'max'])} måneder.`
            );
          } else {
            remarks.push('Lejemålet er uden tidsbegrænsning.');
          }
          break;
        }
        default:
          break;
      }
    }

    const dwellingType = mapTypeDwelling(
      dwelling.getIn(['type']) && dwelling.getIn(['type', 'dwelling'])
    );

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        <div style={{ maxWidth: 800, margin: 'auto' }}>
          <Card>
            <CardHeader
              title={dwelling
                .getIn(['location', 'address'])
                ?.replace(`, ${zipCity}`, '')}
              subheader={zipCity}
              avatar={
                <Avatar>{dwelling.getIn(['location', 'address'])?.[0]}</Avatar>
              }
            />
            {media.length ? (
              <>
                <CardMedia>
                  <div className={styles.mediaContainer}>
                    {media.map((item, idx) =>
                      idx === this.state.currentView ? (
                        <div
                          key={idx}
                          className={styles.mediaContent}
                          style={{
                            border: `4px solid ${this.getValidationBorderColor(item)}`
                          }}
                          title={item.reason || ''}
                        >
                          <img
                            src={item.src}
                            alt={item.reason || `Media ${idx + 1}`}
                            className={styles.mediaImage}
                          />
                        </div>
                      ) : null
                    )}
                  </div>
                </CardMedia>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
                    gap: 8,
                    padding: 8
                  }}
                >
                  {media.map((item, idx) => (
                    <div
                      key={`thumb-${idx}`}
                      style={{
                        height: 56,
                        borderRadius: 4,
                        backgroundImage: `url(${item.src})`,
                        backgroundSize: 'cover',
                        backgroundPosition: '50% 50%',
                        cursor: 'pointer',
                        opacity: idx === this.state.currentView ? 1 : 0.8,
                        boxSizing: 'border-box',
                        border: `2px solid ${this.getValidationBorderColor(
                          item
                        )}`,
                        outline:
                          idx === this.state.currentView
                            ? `2px solid ${this.getSelectedBorderColor(true)}`
                            : 'none',
                        outlineOffset: '0'
                      }}
                      onClick={() => this.handleThumbClick(idx)}
                      title={item.reason || `Image ${idx + 1}`}
                    />
                  ))}
                </div>
              </>
            ) : (
              <p style={{ textAlign: 'center' }}>No Images</p>
            )}
            <CardContent>
              <TearSheet
                style={{ float: 'right', paddingRight: 0, paddingTop: 0 }}
              >
                <List subheader={<div />}>
                  <ListItem dense>
                    <ListItemText
                      primary={withCurrency(
                        dwelling.getIn(['economy', 'rent'])
                      )}
                      secondary={this.props.i18n('Rent')}
                    />{' '}
                  </ListItem>

                  <ListItem dense>
                    <ListItemText
                      primary={withCurrency(
                        dwelling.getIn(['economy', 'prepaid'])
                      )}
                      secondary={this.props.i18n('Prepaid rent')}
                    />
                  </ListItem>
                  <ListItem dense divider>
                    <ListItemText
                      primary={withCurrency(
                        dwelling.getIn(['economy', 'deposit'])
                      )}
                      secondary={this.props.i18n('Deposit')}
                    />
                  </ListItem>
                  <ListSubheader>
                    {this.props.i18n('Utility costs etc.')}
                  </ListSubheader>
                  {economy.inclusive !== true ? (
                    economy.utilities
                      .filter((item) => item.value)
                      .map((item) => (
                        <ListItem
                          dense
                          key={item.key}
                          style={{ paddingTop: 8, paddingBottom: 8 }}
                        >
                          <ListItemText
                            primary={withCurrency(item.value)}
                            secondary={this.props.i18n(item.key)}
                          />
                        </ListItem>
                      ))
                  ) : (
                    <ListItem dense style={{ paddingTop: 8, paddingBottom: 8 }}>
                      <ListItemText primary={'Inklusiv i huslejen'} />
                    </ListItem>
                  )}
                </List>
              </TearSheet>
              <Typography variant="h5">
                {dwelling.getIn(['headline'])}
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                {dwelling.getIn(['size', 'rooms'])
                  ? `${dwelling.getIn(['size', 'rooms'])} vær. `
                  : ''}
                {`${
                  dwelling.getIn(['size', 'rooms'])
                    ? dwellingType
                    : dwellingType.charAt(0).toUpperCase() +
                      dwellingType.slice(1)
                }`}
                {dwelling.getIn(['size', 'area'])
                  ? ` på ${dwelling.getIn(['size', 'area'])} m2`
                  : ''}
              </Typography>
              <div style={{ marginTop: '1em', marginBottom: '1em' }}>
                {showAIDescription && hasAIDescription ? (
                  <div>
                    <h5>AI Description</h5>
                    <Typography style={{ whiteSpace: 'pre-line' }}>
                      {descriptionAI}
                    </Typography>
                  </div>
                ) : null}
                {showNormalDescription && hasNormalDescription ? (
                  <div>
                    <h5>{isAdmin ? 'Original Description' : 'Description'}</h5>
                    <Typography style={{ whiteSpace: 'pre-line' }}>
                      {description}
                    </Typography>
                  </div>
                ) : null}

                {isAdmin && hasAIDescription ? (
                  <Mutation mutation={UPDATE_DWELLING}>
                    {(mutate) => {
                      return (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            marginTop: 12,
                            flexWrap: 'wrap'
                          }}
                        >
                          <Button
                            flat
                            onClick={() => {
                              if (useDescriptionAI) {
                                this.setState((prevState) => ({
                                  showNormalDescription: !prevState.showNormalDescription
                                }));
                              } else {
                                this.setState((prevState) => ({
                                  showAIDescription: !prevState.showAIDescription
                                }));
                              }
                            }}
                            label={
                              useDescriptionAI
                                ? this.state.showNormalDescription
                                  ? this.props.i18n('Hide original description')
                                  : this.props.i18n('Show original description')
                                : this.state.showAIDescription
                                ? this.props.i18n('Hide AI description')
                                : this.props.i18n('Show AI description')
                            }
                          />

                          <label
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              gap: 8
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={useDescriptionAI}
                              onChange={async (ev) => {
                                const checked = ev.target.checked;

                                const res = await mutate({
                                  dwellingId: this.props.dwelling.get('uuid'),
                                  input: { useDescriptionAI: checked }
                                });

                                if (res && res.errors) {
                                  toastr.error(
                                    this.props.i18n(
                                      'Der skete en fejl. Beskrivelsen kunne ikke opdateres.'
                                    )
                                  );
                                  return;
                                }

                                this.setState({
                                  useDescriptionAI: checked,
                                  showNormalDescription: false,
                                  showAIDescription: false
                                });

                                if (this.props.setValue) {
                                  this.props.setValue(
                                    'useDescriptionAI',
                                    checked
                                  );
                                }

                                toastr.success(
                                  this.props.i18n(
                                    'Valg af beskrivelse opdateret'
                                  )
                                );
                              }}
                            />
                            <span>{this.props.i18n('Use AI description')}</span>
                          </label>
                        </div>
                      );
                    }}
                  </Mutation>
                ) : null}
              </div>
              {remarks.length ? (
                <Typography style={{ fontStyle: 'italic' }}>
                  {remarks.join(' ')}
                </Typography>
              ) : null}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  padding: '2em 0 0'
                }}
              >
                {dwelling.getIn(['shareable']) ? (
                  <Chip
                    template="success"
                    icon="group"
                    label="Share friendly"
                    spacing
                  />
                ) : null}
                {dwellingJs.terms
                  .sort((a, b) => {
                    const ORDER = [
                      'PETS',
                      'FURNISED',
                      'SUBLEASE',
                      'RESIDENCEDUTY',
                      'BUSINESSCONTRACT'
                    ];
                    return ORDER.indexOf(a.key) - ORDER.indexOf(b.key);
                  })
                  .filter((t) => {
                    return (
                      t.value !== null &&
                      !(
                        [
                          'BUSINESSCONTRACT',
                          'SUBLEASE',
                          'RESIDENCEDUTY',
                          'FURNISHED'
                        ].includes(t.key) && !t.value
                      )
                    );
                  })
                  .map((t) => {
                    const value =
                      t.value === null
                        ? null
                        : typeof t.value === 'boolean'
                        ? t.value
                        : serializeTerms(t.value);
                    const template = ['BUSINESSCONTRACT'].includes(t.key)
                      ? 'warning'
                      : ['RESIDENCEDUTY', 'SUBLEASE'].includes(t.key)
                      ? 'info'
                      : value
                      ? 'success'
                      : 'danger';
                    return (
                      <Chip key={t.key} template={template} label={t.key} />
                    );
                  })}
              </div>
              <Divider style={{ marginBottom: '0.35em' }} />
              <Typography variant="body1" color="textSecondary" gutterBottom>
                Kontakt
              </Typography>
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {dwelling.getIn(['contact', 'name']) ? (
                  <Chip
                    spacing
                    selectable
                    icon="person"
                    label={dwelling.getIn(['contact', 'name'])}
                  />
                ) : null}
                {dwelling.getIn(['contact', 'phone']) ? (
                  <Chip
                    spacing
                    selectable
                    icon={
                      anonymous.includes('PHONE')
                        ? 'phone_disabled'
                        : 'phone_enabled'
                    }
                    label={dwelling.getIn(['contact', 'phone'])}
                  />
                ) : null}
                {dwelling.getIn(['contact', 'email']) ? (
                  <Chip
                    spacing
                    selectable
                    icon="email"
                    label={dwelling.getIn(['contact', 'email'])}
                  />
                ) : null}
                {dwelling.getIn(['contact', 'link']) ? (
                  <Chip
                    spacing
                    selectable
                    icon="link"
                    label={dwelling.getIn(['contact', 'link'])}
                  />
                ) : null}
              </div>

              <Divider style={{ clear: 'both' }} />
              {!dwellingJs.fromFacebook
                ? [
                    {
                      key: 'TERMS_OF_AGREEMENT',
                      label: ({ handleOpen }) => (
                        <>
                          Jeg accepterer hermed de generelle{' '}
                          <a href="#" onClick={handleOpen}>
                            vilkår for anvendelse af Akutbolig.dk's
                            udlejerplatform
                          </a>
                        </>
                      ),
                      title: '',
                      getContent: () => {
                        const Terms = React.lazy(() =>
                          import(/* webpackPrefetch: true */ '../terms.mdx')
                        );
                        const PrivacyPolicy = React.lazy(() =>
                          import(
                            /* webpackPrefetch: true */ '../privacy-policy.mdx'
                          )
                        );
                        return (
                          <React.Suspense fallback={<div>Loading...</div>}>
                            <Terms />
                            <hr />
                            <PrivacyPolicy />
                          </React.Suspense>
                        );
                      }
                    },
                    {
                      key: 'TERMS_OF_SERVICE',
                      label:
                        'Jeg bekræfter hermed, at oplysningerne i annoncen er korrekte, og at jeg har tilladelse til at udleje det annoncerede lejemål.'
                    },
                    {
                      key: 'COPYRIGHT',
                      label:
                        'Jeg bekræfter hermed, at jeg har rettighederne til annoncens tekst og billeder, og at dette må anvendes til brug på andre platforme.'
                    }
                  ]
                    .filter(Boolean)
                    .map(({ key, ...props }) => {
                      return (
                        <CheckboxDialog
                          key={key}
                          formKey={key}
                          name="consents[]"
                          error={Boolean(
                            this.props.errors &&
                              this.props.errors['consents'] &&
                              this.props.errors['consents'][key]
                          )}
                          defaultValue={consents}
                          readOnly={isAdmin || !this.props.handleChangeMerge}
                          onChange={this.props.handleChangeMerge}
                          removeError={this.props.removeError}
                          {...props}
                        />
                      );
                    })
                : null}
              <Divider style={{ marginBottom: '0.35em' }} />
              <Typography variant="body1" color="textSecondary" gutterBottom>
                Status for annoncen
              </Typography>
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {!dwelling.getIn(['active']) ? (
                  <Chip template="danger" spacing label="Deactivated" />
                ) : dwelling.getIn(['status']) === 'APPROVED' ? (
                  <Chip template="success" spacing label="Active" />
                ) : null}
                {(dwelling && dwelling.getIn(['status']) === 'APPROVED') ||
                (user &&
                  user.get('type') === 'BUSINESS' &&
                  user &&
                  user.get('verified') === true &&
                  dwelling &&
                  dwelling.get('status') === 'PENDING_REVIEW') ? (
                  <Chip template="approved" spacing />
                ) : dwelling.getIn(['status']) === 'PENDING_REVIEW' ? (
                  <Chip template="pending_review" spacing />
                ) : dwelling.getIn(['status']) === 'DOCUMENTATION_REQUESTED' ? (
                  <Chip template="documentation_requested" spacing />
                ) : dwelling.getIn(['status']) === 'REJECTED' ? (
                  <Chip template="rejected" spacing />
                ) : (
                  <Chip template="draft" spacing />
                )}
                <Chip template={dwelling.get('listingType')} spacing />

                {isAdmin && dwelling.get('payment') && (
                  <Chip
                    label={`${dwelling.getIn(['payment', 'product'])}: ${
                      dwelling.getIn(['payment', 'amount']) / 100
                    } ${dwelling.getIn(['payment', 'currency'])}`}
                    template={
                      dwelling.getIn(['payment', 'captured']) === true
                        ? 'success'
                        : 'warning'
                    }
                    spacing
                    icon="payment"
                  />
                )}

                {dwelling.getIn(['locked']) ? (
                  <Chip
                    template="warning"
                    spacing
                    icon="lock_outline"
                    label="Locked for changes"
                  />
                ) : null}
                {isAdmin ? (
                  <Mutation mutation={UPDATE_DWELLING}>
                    {(mutate) => {
                      return (
                        <SelectField
                          label={this.props.i18n('Annoncetype')}
                          options={[
                            {
                              value: 'STANDARD',
                              label: this.props.i18n('Standard')
                            },
                            {
                              value: 'PLUS',
                              label: this.props.i18n('Plus')
                            },
                            {
                              value: 'PREMIUM',
                              label: this.props.i18n('Premium')
                            }
                          ]}
                          defaultValue={dwelling.get('listingType')}
                          name="listingType"
                          onChange={async (ev) => {
                            const { name, value } = ev.target;
                            const res = await mutate({
                              dwellingId: this.props.dwelling.get('uuid'),
                              input: { [name]: value }
                            });
                            if (res && res.errors) {
                              toastr.error(
                                this.props.i18n(
                                  'Der skete en fejl. Annoncen kunne ikke opdateres.'
                                )
                              );
                              return;
                            }
                            if (['PLUS', 'PREMIUM'].includes(value)) {
                              // goal(`manual-${value.toLowerCase()}-sale`);
                            }
                            this.props.setValue(
                              'listingType',
                              res &&
                                res.data &&
                                res.data.updateDwelling.listingType
                            );
                            toastr.success(
                              this.props.i18n('Annoncetype ændret')
                            );
                          }}
                          fullWidth
                        />
                      );
                    }}
                  </Mutation>
                ) : null}
              </div>

              {showActions ? <Divider style={{ clear: 'both' }} /> : null}
              {isAdmin ? (
                <>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    gutterBottom
                  >
                    Brugerens info
                  </Typography>
                  <Query
                    query={DWELLING}
                    variables={{ dwellingId: this.props.dwelling.get('uuid') }}
                  >
                    {(res) => {
                      const user =
                        res &&
                        res.data &&
                        res.data.dwelling &&
                        res.data.dwelling.user;
                      return (
                        <>
                          <Chip
                            spacing
                            selectable
                            icon="people"
                            label={(user && user.type) || ''}
                          />
                          <Chip
                            spacing
                            selectable
                            icon="person"
                            label={(user && user.name) || ''}
                          />
                          <Chip
                            spacing
                            selectable
                            icon="email"
                            label={(user && user.email) || ''}
                            template={
                              user && user.emailVerified ? 'verified' : ''
                            }
                          />
                          <Chip
                            spacing
                            selectable
                            icon="phone_enabled"
                            label={(user && user.phone) || ''}
                            template={
                              user && user.phoneVerified ? 'verified' : ''
                            }
                          />
                          <br />
                          <Typography
                            variant="body1"
                            color="textSecondary"
                            gutterBottom
                          >
                            Andre lejemål for brugeren
                          </Typography>
                          <DwellingsTable
                            admin
                            userId={this.props.dwelling.get('user')}
                            rowsPerPage={10}
                          />
                        </>
                      );
                    }}
                  </Query>
                </>
              ) : null}
            </CardContent>

            {showActions ? (
              isAdmin ? (
                <>
                  <CardActions style={{ paddingBottom: 6 }}>
                    <Button
                      flat
                      icon="chevron_left"
                      label={isPalm ? '' : this.props.i18n('Back')}
                      onClick={this.handleClose}
                    />
                    <Button
                      primary
                      flat
                      icon="edit"
                      label={isPalm ? '' : this.props.i18n('Edit')}
                      onClick={this.handleEdit}
                    />
                    <ActivitiesDialog
                      uuid={dwelling.getIn(['uuid'])}
                      icon="list"
                      label={isPalm ? '' : this.props.i18n('Log')}
                    />

                    <Button
                      flat
                      label={'Forlæng'}
                      onClick={this.extendDwelling}
                    />

                    <div style={{ flex: '1 1 auto' }} />

                    {dwelling.getIn(['status']) !== 'APPROVED' &&
                      dwelling.getIn(['active']) ? (
                        <>
                          <ConfirmDialog
                            onConfirm={this.toggleActivation}
                            buttonProps={{
                              secondary: true,
                              flat: true,
                              icon: 'delete',
                              label: isPalm ? '' : this.props.i18n('Deactivate')
                            }}
                          />
                          <ValidationDialog
                            uuid={dwelling.getIn(['uuid'])}
                            primary
                            icon="done"
                            label={isPalm ? '' : 'Godkend/afvis'}
                            onApprove={this.handleApprove}
                            onReject={this.handleReject}
                          />
                        </>
                      ) : dwelling.getIn(['status']) === 'APPROVED' &&
                        !dwelling.getIn(['active']) ? (
                        <Button
                          primary
                          icon="done"
                          label={isPalm ? '' : this.props.i18n('Activate')}
                          onClick={this.toggleActivation}
                        />
                      ) : dwelling.getIn(['active']) ? (
                        <Button
                          secondary
                          icon="delete"
                          label={isPalm ? '' : this.props.i18n('Deactivate')}
                          onClick={this.toggleActivation}
                        />
                      ) : null}
                  </CardActions>
                </>
              ) : (
                <CardActions style={{ paddingBottom: 6 }}>
                  {dwelling.getIn(['active']) ? (
                    <Button
                      secondary
                      icon="delete"
                      label={isPalm ? '' : this.props.i18n('Deactivate')}
                      style={{ float: 'right', marginRight: 0 }}
                      onClick={this.toggleActivation}
                    />
                  ) : null}
                  <Button
                    primary
                    flat
                    icon="close"
                    label={isPalm ? '' : this.props.i18n('Close')}
                    onClick={this.handleClose}
                  />
                  {!dwelling.getIn(['locked']) ? (
                    <Button
                      secondary
                      flat
                      icon="edit"
                      label={isPalm ? '' : this.props.i18n('Edit')}
                      onClick={this.handleEdit}
                    />
                  ) : null}
                </CardActions>
              )
            ) : null}
          </Card>
        </div>
        {showAdminPanel && isAdmin && (
          <div
            style={{
              maxWidth: 500,
              marginLeft: 'auto',
              marginRight: 'auto',
              paddingTop: '20px'
            }}
          >
            <AdminPanel dwelling={dwellingJs} />
          </div>
        )}
      </div>
    );
  }
}

function DwellingPreviewWithRouter(props) {
  const navigate = useNavigate();
  const location = useLocation();
  return <DwellingPreview {...props} navigate={navigate} location={location} />;
}

export default DwellingPreviewWithRouter;
